import styled from "styled-components";
import { colors, font, radius, shadow } from "../../../styles/theme/tokens";

export const Page = styled.div`
  font-family: ${font.text};
  color: ${colors.ink};
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const Card = styled.section`
  background: ${colors.surface};
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.card};
  padding: 18px 20px;
  min-width: 0;
`;

export const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

export const H = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
`;

export const Hint = styled.p`
  margin: 4px 0 0;
  font-size: 0.82rem;
  color: ${colors.muted};
`;

export const Row2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

export const PeriodBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
`;

export const RoundBtn = styled.button`
  border: 1px solid ${colors.line};
  background: ${colors.surface};
  color: ${colors.ink};
  border-radius: ${radius.pill};
  width: 34px;
  height: 34px;
  cursor: pointer;
  &:disabled { opacity: 0.35; cursor: default; }
`;

export const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  @media (max-width: 800px) { grid-template-columns: repeat(2, 1fr); }
`;

export const Step = styled.div`
  background: ${colors.brandSoft};
  border-radius: ${radius.md};
  padding: 14px;
  position: relative;
`;

export const Big = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${colors.brandDark};
`;

export const Small = styled.div`
  font-size: 0.82rem;
  color: ${colors.muted};
`;

export const Rate = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: ${radius.pill};
  background: ${colors.surface};
  font-size: 0.78rem;
  font-weight: 700;
  color: ${colors.brandDeep};
`;

export const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
`;

export const Stat = styled.div`
  background: ${(p) => (p.$tone === "amber" ? colors.amberSoft : p.$tone === "pink" ? colors.pinkSoft : colors.bg)};
  border-radius: ${radius.md};
  padding: 12px;
`;

export const Bars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const BarRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 40px;
  align-items: center;
  gap: 10px;
  font-size: 0.88rem;
`;

export const BarTrack = styled.div`
  height: 10px;
  border-radius: ${radius.pill};
  background: ${colors.bg};
  overflow: hidden;
  & > i { display: block; height: 100%; background: ${colors.brand}; border-radius: ${radius.pill}; }
`;

export const Table = styled.div`
  width: 100%;
  overflow-x: auto;
  table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  th { text-align: left; color: ${colors.muted}; font-weight: 700; padding: 8px 6px; border-bottom: 1px solid ${colors.line}; white-space: nowrap; }
  td { padding: 8px 6px; border-bottom: 1px solid ${colors.line}; vertical-align: middle; }
`;

export const Select = styled.select`
  border: 1px solid ${colors.line};
  border-radius: ${radius.sm};
  padding: 6px 8px;
  font: inherit;
  background: ${colors.surface};
  color: ${colors.ink};
`;

export const Input = styled.input`
  border: 1px solid ${colors.line};
  border-radius: ${radius.sm};
  padding: 7px 10px;
  font: inherit;
  min-width: 0;
  background: ${colors.surface};
  color: ${colors.ink};
`;

export const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 10px;
  align-items: end;
  padding: 12px;
  border-radius: ${radius.md};
  background: ${colors.bg};
  margin-bottom: 14px;
  label { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; font-weight: 700; color: ${colors.muted}; }
`;

export const Btn = styled.button`
  border: none;
  border-radius: ${radius.sm};
  padding: 9px 14px;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
  background: ${(p) => (p.$ghost ? colors.surface : colors.brand)};
  color: ${(p) => (p.$ghost ? colors.ink : "#fff")};
  border: ${(p) => (p.$ghost ? `1px solid ${colors.line}` : "none")};
  &:hover { filter: brightness(0.96); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const Tag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${radius.pill};
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => ({ green: colors.successSoft, red: colors.dangerSoft, amber: colors.amberSoft, pink: colors.pinkSoft }[p.$c] || colors.brandSoft)};
  color: ${(p) => ({ green: colors.success, red: colors.danger, amber: colors.amberDeep, pink: colors.pink }[p.$c] || colors.brandDeep)};
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  li { display: flex; justify-content: space-between; gap: 10px; padding: 7px 0; border-bottom: 1px solid ${colors.line}; font-size: 0.9rem; }
  li:last-child { border-bottom: none; }
`;

export const Empty = styled.p`
  color: ${colors.muted};
  font-size: 0.88rem;
  margin: 6px 0;
`;

export const ErrorText = styled.p`
  color: ${colors.danger};
  font-size: 0.85rem;
  margin: 6px 0;
`;
